import * as Radix from "@radix-ui/themes";
import { Vivid, Components } from "@vendrinc/frontend-ts";
import React from "react";
import styles from "./Footer.module.css";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import * as Analytics from "~/hooks/useAnalytics";
import * as Image from "~/image";
const { Icon } = Vivid;
const { PageWrapper } = Components;

const LinkGroupColumn: React.FC<{ children?: React.ReactNode }> = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  return (
    <Radix.Flex
      direction="column"
      flexGrow="1"
      flexShrink="1"
      pb={{ initial: "6" }}
      pl={{ initial: "3" }}
    >
      {children}
    </Radix.Flex>
  );
};

const LinkGroup: React.FC<{ children?: React.ReactNode }> = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  return (
    <Radix.Flex direction="column" justify="center" align="start">
      {children}
    </Radix.Flex>
  );
};

const LinkGroupTitle = ({ label }: { label: string }) => (
  <Radix.Box
    mb={{ initial: "2", md: "4" }}
    className={styles["link-group-title"]}
  >
    {label}
  </Radix.Box>
);

const LinkGroupLink = ({
  label,
  href,
  children,
}: {
  label: string;
  href: string;
  children?: React.ReactNode;
}) => {
  const { trackLink } = Analytics.useAnalytics();

  const { Link } = React.useContext(ReactRouterStubsContext);

  return (
    <Link
      to={href}
      className={styles["footer-link"]}
      onClick={() => {
        trackLink({
          element_text: label,
          element_location: "Footer",
        });
      }}
      reloadDocument={true}
    >
      <Radix.Box my={{ initial: "1" }}>
        {label}
        {children}
      </Radix.Box>
    </Link>
  );
};

const SocialLink: React.FC<{
  label: string;
  href: string;
  src: string;
}> = ({ label, href, src }) => {
  const { trackLink } = Analytics.useAnalytics();

  return (
    <Radix.Box p={{ initial: "1", md: "4" }} className={styles["social-icon"]}>
      <Radix.Link
        onClick={() => {
          trackLink({
            element_text: label,
            element_location: "Footer social links",
          });
        }}
        href={href}
      >
        <img src={src} alt={label} height="Auto" loading="lazy"></img>
      </Radix.Link>
    </Radix.Box>
  );
};

const LinkGroupPlatform = () => (
  <LinkGroup>
    <LinkGroupTitle label="Platform" />
    <LinkGroupLink label="Intelligence" href="/intelligence" />
    <LinkGroupLink label="Pricing" href="/pricing" />
    <LinkGroupLink label="Book a Demo" href="/get-demo" />
  </LinkGroup>
);

const LinkGroupBuyer = () => (
  <LinkGroup>
    <LinkGroupTitle label="Buyers" />
    <LinkGroupLink label="Marketplace" href="/marketplace" />
    <LinkGroupLink label="Categories" href="/categories" />
  </LinkGroup>
);

const LinkGroupEducation = () => (
  <LinkGroup>
    <LinkGroupTitle label="Education" />
    <LinkGroupLink label="Blog" href="/blog" />
    <LinkGroupLink label="Customers" href="/customers" />
    <LinkGroupLink
      label="2025 SaaS Trends Report"
      href="/insights/saas-trends-report-2025"
    />

    <LinkGroupLink label="Buyer Guides" href="/buyer-guides" />
    <LinkGroupLink label="Events &amp; Webinars" href="/events" />
  </LinkGroup>
);

const LinkGroupLifeAtVendr = () => (
  <LinkGroup>
    <LinkGroupTitle label="Life at Vendr" />
    <LinkGroupLink label="About us" href="/about" />
    <LinkGroupLink label="Careers" href="/careers" />
  </LinkGroup>
);

const LinkGroupLegal = () => (
  <LinkGroup>
    <LinkGroupTitle label="Legal" />
    <LinkGroupLink label="Terms" href="/legal/terms-of-use" />
    <LinkGroupLink label="Privacy" href="/legal/privacy-policy" />
    <LinkGroupLink label="Subprocessors" href="/legal/data-subprocessors" />
    <LinkGroupLink label="Cookies" href="/legal/cookie-policy" />
    <LinkGroupLink
      label="Data Processing Addendum"
      href="/legal/data-processing-addendum"
    />
    <LinkGroupLink label="Master Services Agreement" href="/legal/plus-msa" />
    <LinkGroupLink label="Security" href="/legal/security" />
    <LinkGroupLink label="Privacy Request Form " href="/privacy-request-form">
      <span className="text-size-small">↗</span>
    </LinkGroupLink>

    <div className={styles["phone-number"]}>
      {"or call +1 (888) 688-4404 toll-free"}
    </div>
  </LinkGroup>
);

const SubscribeLink = () => {
  const { trackLink } = Analytics.useAnalytics();

  return (
    <Radix.Link
      href="/subscribe"
      className={styles["subscribe-link"]}
      onClick={() => {
        trackLink({
          element_text: "Subscribe for Vendr updates",
          element_location: "Footer",
        });
      }}
    >
      <Radix.Flex align="center" gap="2">
        {Icon.mail}
        Subscribe for Vendr updates
      </Radix.Flex>
    </Radix.Link>
  );
};

export const Footer = () => {
  const elem = (
    <footer className={styles.footer}>
      <PageWrapper>
        <Radix.Flex width="auto" direction="column">
          <Radix.Box flexBasis="90%">
            <Radix.Container maxWidth="100%">
              <Radix.Flex
                direction={{ initial: "column", sm: "column", md: "row" }}
                justify="between"
                wrap="wrap"
              >
                <Radix.Flex
                  direction="column"
                  justify="between"
                  flexBasis={{ initial: "100%", md: "unset" }}
                  flexGrow={"1"}
                  flexShrink={"0"}
                  pb="6"
                  px={{ initial: "2" }}
                >
                  <Radix.Box>
                    <div>
                      <img
                        src="https://cdn.prod.website-files.com/62e82d18db90171054c0ba43/647725443db454a258a991cd_vendr_logo_duo-black.svg"
                        loading="lazy"
                        width="125"
                        alt="Vendr logo"
                      />
                      <p className="footer-p"></p>
                    </div>
                  </Radix.Box>
                </Radix.Flex>
                <LinkGroupColumn>
                  <LinkGroupPlatform />
                  <div className={styles["footer-divider"]} />
                  <LinkGroupBuyer />
                </LinkGroupColumn>
                <LinkGroupColumn>
                  <LinkGroupEducation />
                  <div className={styles["footer-divider"]} />
                  <LinkGroupLifeAtVendr />
                </LinkGroupColumn>
                <LinkGroupColumn>
                  <LinkGroupLegal />
                </LinkGroupColumn>
              </Radix.Flex>
            </Radix.Container>
          </Radix.Box>
          <Radix.Box flexBasis="10%">
            <Radix.Separator size="4" className={styles["footer-divider"]} />
          </Radix.Box>
          <Radix.Box flexBasis="10%">
            <Radix.Flex
              direction="row"
              justify="between"
              wrap="wrap"
              px={{ initial: "2" }}
            >
              <Radix.Box
                mt={{ initial: "0", md: "4" }}
                mb={{ initial: "4", md: "0" }}
                flexBasis={{ initial: "100%", md: "50%" }}
              >
                <SubscribeLink />
              </Radix.Box>
              <Radix.Box>
                <Radix.Flex
                  direction="row"
                  justify="between"
                  flexBasis={{ initial: "100%", md: "50%" }}
                >
                  <SocialLink
                    href="https://twitter.com/vendrhq?lang=en"
                    src={Image.socialTwitter}
                    label="Twitter icon"
                  />

                  <SocialLink
                    href="https://www.facebook.com/savewithvendr/"
                    src={Image.socialFacebook}
                    label="Facebook icon"
                  />
                  <SocialLink
                    href="https://www.linkedin.com/company/vendr-co"
                    src={Image.socialLinkedIn}
                    label="LinkedIn icon"
                  />
                </Radix.Flex>
              </Radix.Box>
            </Radix.Flex>
          </Radix.Box>
        </Radix.Flex>
      </PageWrapper>
    </footer>
  );

  return elem;
};
